import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useControllers } from './hooks';
import { RemoveExclusionFormValues } from './types';
import { campaignController } from '@/apis/core';
import { ExcludedMno } from '@/apis/core/types';
import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';

interface RemoveExclusionModalProps {
  open?: boolean;
  campaignUid: string;
  mno: ExcludedMno;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
    {children}
  </Flex>
);

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      padding: '12px 0',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '100%',
      padding: '7px 0 11px 0',
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H300',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 'l',
      padding: '30px 0 40px',
    }}
  >
    {children}
  </Flex>
);

const RemoveExclusionModal: FunctionComponent<RemoveExclusionModalProps> = ({
  open = false,
  campaignUid,
  mno,
  onCancel,
  onSubmit,
}) => {
  const {
    formState: { errors },
    control,
    reset,
    handleSubmit,
  } = useForm<RemoveExclusionFormValues>({
    defaultValues: {
      explanation: '',
    },
  });
  const { explanation, setExplanation, validateExplanation, explanationError } =
    useControllers(control);
  const [proceeding, setProceeding] = useState(false);

  const handleValid = async () => {
    setProceeding(true);
    const response = await campaignController.removeMnoExclusion(
      campaignUid,
      mno.id,
      explanation
    );
    if (response) {
      toastFlashMessage(
        `${mno.name} exclusion removed successfully`,
        'success'
      );
      onSubmit?.();
    }
    setProceeding(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open}>
      <form noValidate onSubmit={handleSubmit(handleValid)}>
        <Container>
          <Title>Remove Exclusion</Title>
          <Message>
            Please confirm that you want to remove this exclusion. This action
            cannot be undone and new exclusions cannot be added later.
          </Message>
          <TextInput
            style={{ width: '100%' }}
            multiline
            error={!!explanationError?.message}
            minRows={3}
            maxRows={3}
            maxLength={2048}
            label="Explanation (Required)"
            note={explanationError?.message}
            value={explanation}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
          <Actions>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                proceeding ||
                Object.values(errors).some((error) => !!error.message)
              }
            >
              Confirm
            </Button>
          </Actions>
        </Container>
      </form>
    </Modal>
  );
};

export default RemoveExclusionModal;
